/**
 * useOtpProtectedProfileUpdate — orchestrates PATCH /profile/update with OTP.
 *
 * Backend contract:
 *   • A first PATCH with sensitive field changes (mobile_e164 / email / pan)
 *     responds with { otp_required: true, otp_target, exp }. The same exact
 *     payload must be re-sent with `otp: <code>` to finalize.
 *   • Non-sensitive changes complete in one PATCH.
 *
 * This hook holds the pending payload and exposes:
 *   • phase       — 'idle' | 'awaiting-otp' | 'verifying'
 *   • challenge   — { otp_target, exp } | null
 *   • submit(payload)
 *   • verify(otp)
 *   • cancel()
 *
 * The consumer (the EditProfileForm) decides what to do on success/error.
 */
import * as React from 'react';
import { toast } from '@finmin/ui';
import {
  useUpdateProfile,
  type ProfileUpdateRequest,
  type ProfileUpdateResponse,
} from '@/api/profile';

type OtpPhase = 'idle' | 'awaiting-otp' | 'verifying';

interface ChallengeState {
  otp_target: string | null;
  exp:        number;
}

export interface UseOtpProtectedProfileUpdateOptions {
  onUpdated?: (response: ProfileUpdateResponse & { updated: true }) => void;
}

export function useOtpProtectedProfileUpdate(
  opts: UseOtpProtectedProfileUpdateOptions = {},
) {
  const [phase,       setPhase]      = React.useState<OtpPhase>('idle');
  const [challenge,   setChallenge]  = React.useState<ChallengeState | null>(null);
  const pendingRef                    = React.useRef<ProfileUpdateRequest | null>(null);

  const { mutateAsync, isPending, error, reset } = useUpdateProfile();

  async function submit(payload: ProfileUpdateRequest) {
    pendingRef.current = payload;
    try {
      const result = await mutateAsync(payload);

      if (result.otp_required) {
        setChallenge({ otp_target: result.otp_target, exp: result.exp });
        setPhase('awaiting-otp');
        return result;
      }

      pendingRef.current = null;
      setPhase('idle');
      opts.onUpdated?.(result);
      return result;
    } catch (err) {
      pendingRef.current = null;
      setPhase('idle');
      throw err;
    }
  }

  /** Re-submit the held payload with an OTP code. */
  async function verify(otp: string) {
    if (!pendingRef.current) {
      toast.error('Session expired — please retry the update.');
      setPhase('idle');
      return;
    }
    setPhase('verifying');
    try {
      const result = await mutateAsync({ ...pendingRef.current, otp });

      if (result.otp_required) {
        toast.error('That code didn\'t verify. Try again.');
        setPhase('awaiting-otp');
        return result;
      }

      pendingRef.current = null;
      setChallenge(null);
      setPhase('idle');
      opts.onUpdated?.(result);
      return result;
    } catch (err) {
      setPhase('awaiting-otp');
      throw err;
    }
  }

  function cancel() {
    pendingRef.current = null;
    setChallenge(null);
    setPhase('idle');
    reset();
  }

  return {
    phase,
    challenge,
    isPending,
    error,
    submit,
    verify,
    cancel,
  };
}
